import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import MuiAlert from '@mui/material/Alert';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormGroup,
  IconButton,
  Link,
  List,
  ListItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Close,
  Delete,
  ExpandMore,
  Person2,
} from '@mui/icons-material';
import { PromptProject } from '@safeguardproperties/proto/eventsource/ml/promptproject/v1/promptproject_v1_pb';
import { ProjectClient } from './ProjectClient';
import { PartClient } from './PartClient';
import {
  Prompt,
  PromptType,
  State,
  State as PromptState,
} from '@safeguardproperties/proto/eventsource/ml/prompt/v1/prompt_v1_pb';
import {
  PromptProjectCreateRequest,
  PromptProjectSetOutputPromptRequest,
  PromptProjectSetSystemPromptRequest,
} from '@safeguardproperties/proto/lambda/ml/promptproject/v1/promptproject_v1_pb';
import { useParams } from 'react-router-dom';
import {
  PromptCreateRequest,
  PromptDeactivateRequest,
  PromptReplaceRequest,
  RenderRequest,
} from '@safeguardproperties/proto/lambda/ml/prompt/v1/prompt_v1_pb';
import { PromptAdminClient } from './AdminClient';

import {
  PromptAdminExportRequest,
  PromptAdminImportRequest,
} from '@safeguardproperties/proto/lambda/ml/promptadmin/v1/promptadmin_v1_pb';
import CircularProgress from '@mui/material/CircularProgress';

function generatePromptId(project: string, promptId: string): string {
  return `PROJECT:${project}$PROMPTID:${promptId}`;
}
const domain = globalThis.location.host
  .split('.')
  .slice(1)
  .join('.')
  .split(':')[0];
const client = new ProjectClient(
  `https://promptapi.${domain}/promptproject/v1/`,
  true
);
const admin = new PromptAdminClient(
  `https://promptapi.${domain}/promptadmin/v1/`,
  true
);
const pmpt = new PartClient(`https://promptapi.${domain}/prompt/v1/`, true);

const PromptProjectForm = () => {
  const [project, setProject] = useState<PromptProject | null>(null);
  const [description, setDescription] = useState(project?.description || '');
  const [systemPrompt, setSystemPrompt] = useState(project?.systemPrompt || '');
  const [outputPrompt, setOutputPrompt] = useState(project?.outputPrompt || '');
  const [id, setId] = useState(project?.id || '');
  const [state, setState] = useState<boolean>(
    project?.state == State.ACTIVE || true
  );
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [parts, setParts] = useImmer<Prompt[]>([]);
  const params = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'success' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [importOpen, setImportOpen] = useState(false);
  const [partsLoading, setPartsLoading] = useState(false);

  interface EditPrompt {
    id?: string;
    name: string;
    description: string;
    type: PromptType;
    context: string[];
  }

  const filteredParts = parts.filter((part) => {
    const search = searchTerm.toLowerCase();

    return (
      part.state != PromptState.INACTIVE &&
      (part.description.toLowerCase().includes(search) ||
        part.id.toLowerCase().includes(search) ||
        part.name.toLowerCase().includes(search))
    );
  });

  useEffect(() => {
    if (params.id != null && params.id !== '') {
      setPartsLoading(true);
      client
        .GetProject(params.id)
        .then((response) => {
          const proj = response.PromptProject;
          setProject(proj || null);
          setDescription(proj?.description || '');
          setSystemPrompt(proj?.systemPrompt || '');
          setOutputPrompt(proj?.outputPrompt || '');
          setId(proj?.id || '');
          setState(proj?.state == State.ACTIVE);
          pmpt
            .GetProjectPrompts(proj?.id || '')
            .then((response) => {
              setParts(response);
              setPartsLoading(false);
            })
            .catch(() => setPartsLoading(false));
        })
        .catch(() => setPartsLoading(false));
    }
  }, [params.id, setParts]);
  const handleSave = () => {
    setSaveStatus('saving');
    setErrorMessage('');
    if (project === null) {
      client
        .Create(
          new PromptProjectCreateRequest({
            create: {
              outputPrompt: outputPrompt,
              systemPrompt: systemPrompt,
              description: description,
              id: id,
            },
          })
        )
        .then((response) => {
          setTimeout(() => {
            setSaveStatus('success');
            setAlert({
              open: true,
              message: 'Project created successfully.',
              severity: 'success',
            });
            globalThis.location.assign(`./${id}`);
          }, 300);
        })
        .catch((error) => {
          setSaveStatus('error');
          setErrorMessage(error.message || 'Failed to Create project');
          setAlert({
            open: true,
            message: error.message || 'Failed to Create project',
            severity: 'error',
          });
        });
    } else {
      client
        .SetOutputPrompt(
          new PromptProjectSetOutputPromptRequest({
            setOutputPrompt: {
              outputPrompt: outputPrompt,
              id: project?.id,
            },
          })
        )
        .then(() => {
          setAlert({
            open: true,
            message: 'Project saved successfully.',
            severity: 'success',
          });
        })
        .catch((error) => {
          setAlert({
            open: true,
            message: error.message || 'Failed to save project',
            severity: 'error',
          });
        });

      client
        .SetSystemPrompt(
          new PromptProjectSetSystemPromptRequest({
            setSystemPrompt: {
              systemPrompt: systemPrompt,
              id: project?.id,
            },
          })
        )
        .then(() => {
          setSaveStatus('success');
        })
        .catch((error) => {
          setSaveStatus('error');
          setErrorMessage(error.message || 'Failed to update project');
        });
    }
  };

  const handleImportCsv = async (csvText: string) => {
    try {
      const response = await admin.Import(
        new PromptAdminImportRequest({
          promptCsv: csvText,
          promptProjectId: project?.id,
        })
      );
      setAlert({
        open: true,
        message: `Import complete: ${response.createdCount} created, ${response.replacedCount} replaced - reloading in 1 second`,
        severity: 'success',
      });
      setTimeout(() => globalThis.location.reload(), 1000);
    } catch (error: any) {
      setAlert({
        open: true,
        message: `Import failed: ${error?.message || error}`,
        severity: 'error',
      });
    }
  };

  return (
    <Container>
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <MuiAlert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </MuiAlert>
      </Snackbar>
      <Breadcrumbs aria-label='breadcrumb'>
        <Link underline='hover' color='inherit' href='/prompts/'>
          Projects
        </Link>
        <Typography sx={{ color: 'text.primary' }}>
          {project ? project.description : 'Create Project'}
        </Typography>
      </Breadcrumbs>
      <h1>{project ? 'Edit Prompt Project' : 'Create Prompt Project'}</h1>
      <Box
        component='form'
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <FormGroup sx={{ gap: 2 }}>
          {!project && (
            <TextField
              label='ID'
              value={id}
              onChange={(e) => setId(e.target.value)}
              fullWidth
              variant='outlined'
            />
          )}
          <TextField
            label='Description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            variant='outlined'
          />
          <TextField
            label='System Prompt'
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            placeholder='Enter the system prompt here...'
          />
          <TextField
            label='Output Prompt'
            value={outputPrompt}
            onChange={(e) => setOutputPrompt(e.target.value)}
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            placeholder='Enter the output prompt here...'
          />
        </FormGroup>
        {project && (
          <Stack direction='row' spacing={2} alignContent='center'>
            <UserInfo
              label='Created By'
              userName={project.createBy}
              date={project.createAt}
            />
            <UserInfo
              label='Last Modified By'
              userName={project.modifyBy}
              date={project.modifyAt}
            />
            <p></p>
          </Stack>
        )}

        <Button variant='contained' color='primary' onClick={handleSave}>
          Save
        </Button>
      </Box>
      {project && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant='outlined'
              color='primary'
              onClick={() => setImportOpen(true)}
            >
              Import CSV
            </Button>
            <ExportCSVButton project={project} parts={parts} />
          </Box>
          <ImportCSVModal
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImportCsv={handleImportCsv}
          />

          <Box display='flex' alignItems='center' mb={2}>
            <TextField
              label='Search Parts'
              variant='outlined'
              fullWidth
              margin='normal'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Box ml={4}>
              <AddPromptModal project={project!} />
            </Box>
          </Box>

          <Box>
            {partsLoading ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                minHeight={200}
              >
                <CircularProgress />
                <span style={{ marginLeft: 8 }}>Loading</span>
              </Box>
            ) : (
              filteredParts.map((part) => (
                <Accordion key={part.id}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display='flex' flexDirection='column' flexGrow={1}>
                      <Typography variant='h6' fontWeight='bold'>
                        {part.description}
                      </Typography>
                      <Typography variant='caption' color='textSecondary'>
                        ID: {part.id}
                      </Typography>
                    </Box>
                    <Chip
                      label={`v${Number(part?.version)}`}
                      color='primary'
                      size='small'
                    />
                    <Box
                      display='flex'
                      flexDirection='column'
                      alignItems='flex-end'
                      mt={1}
                    >
                      <TrashCanButton
                        promptId={part.id}
                        onAlert={(
                          message: string,
                          severity: 'success' | 'error'
                        ) => setAlert({ open: true, message, severity })}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <PromptEditor
                      project={project!}
                      prompt={part}
                      postSave={() =>
                        setTimeout(() => globalThis.location.reload(), 1000)
                      }
                      onAlert={(message, severity) =>
                        setAlert({ open: true, message, severity })
                      }
                    />
                    {part?.exampleJson != undefined &&
                      part?.exampleJson != '{}' && (
                        <RenderPreview prompt={part!} project={project!} />
                      )}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
};

interface UserInfoProps {
  label: string;
  userName: string;
  date: bigint;
}

const UserInfo: React.FC<UserInfoProps> = ({ label, userName, date }) => {
  return (
    <Box
      display='flex'
      flexDirection='column'
      gap={0.5}
      padding={2}
      border='1px solid #e0e0e0'
      borderRadius='8px'
      bgcolor='#f9f9f9'
      boxShadow='0 2px 4px rgba(0, 0, 0, 0.1)'
    >
      <Typography variant='subtitle2' color='textSecondary'>
        {label}
      </Typography>
      <Box display='flex' alignItems='center' gap={1}>
        <Person2 color='action' />
        <Typography variant='body1' fontWeight='bold'>
          {userName}
        </Typography>
      </Box>
      <Typography variant='caption' color='textSecondary'>
        {new Date(Number(date) * 1000).toLocaleDateString('en-US', {})}{' '}
        {new Date(Number(date) * 1000).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Typography>
    </Box>
  );
};
interface RenderPreviewProps {
  prompt: Prompt;
  project: PromptProject;
}

const RenderPreview: React.FC<RenderPreviewProps> = ({ prompt, project }) => {
  const [parameters, setParameters] = useState(prompt.exampleJson);
  const [preview, setPreview] = useState('');

  const handlePreview = () => {
    pmpt
      .Render(
        new RenderRequest({
          promptProject: project.id,
          key: prompt.id.split(':').at(-1),
          json: parameters,
        })
      )
      .then(({ user }) => setPreview(user));
  };

  return (
    <Box
      sx={{
        margin: 'auto',
        padding: 2,
        boxShadow: 3,
        bgcolor: '#f9f9f9',
      }}
    >
      <Typography variant='h5' gutterBottom>
        Render Preview
      </Typography>
      <TextField
        label='Prompt'
        value={prompt.context.join('')}
        fullWidth
        multiline
        rows={4}
        variant='outlined'
        InputProps={{ readOnly: true }}
        sx={{ marginBottom: 2 }}
      />
      <Stack direction='row' spacing={2} sx={{ marginBottom: 2 }}>
        <TextField
          label='Parameters'
          value={parameters}
          onChange={(e) => setParameters(e.target.value)}
          fullWidth
          multiline
          rows={8}
          variant='outlined'
          InputProps={{
            sx: { fontFamily: 'monospace' },
          }}
        />
        <TextField
          label='Preview'
          value={preview}
          fullWidth
          multiline
          rows={8}
          variant='outlined'
          InputProps={{ readOnly: true }}
        />
      </Stack>
      <Button variant='contained' color='primary' onClick={handlePreview}>
        Preview
      </Button>
    </Box>
  );
};

interface EditPrompt {
  id?: string;
  name: string;
  description: string;
  type: PromptType;
  context: string[];
}

interface PromptEditorProps {
  project: PromptProject;
  prompt?: Prompt;
  postSave?: () => void;
  onAlert?: (message: string, severity: 'success' | 'error') => void;
}

const PromptEditor: React.FC<PromptEditorProps> = ({
  project,
  prompt,
  postSave,
  onAlert,
}) => {
  const [promptObject, setPrompt] = useImmer<EditPrompt>(
    prompt
      ? {
          id: prompt.id,
          name: prompt.name,
          description: prompt.description,
          type: prompt.promptType,
          context: prompt.context,
        }
      : {
          id: '',
          name: '',
          description: '',
          type: PromptType.USER,
          context: [''],
        }
  );
  const [saving, setSaving] = useState(false);
  const handleFieldChange = (field: keyof EditPrompt, value: any) => {
    setPrompt((draft) => {
      // @ts-ignore
      draft[field] = value;
    });
  };

  const handleContextChange = (index: number, value: string) => {
    setPrompt((draft) => {
      draft.context[index] = value;
    });
  };

  const handleAddContext = () => {
    setPrompt((draft) => {
      draft.context.push('');
    });
  };

  const handleRemoveContext = (index: number) => {
    setPrompt((draft) => {
      draft.context.splice(index, 1);
    });
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleSave = async () => {
    setSaving(true);
    try {
      if (prompt == null) {
        await pmpt.Create(
          new PromptCreateRequest({
            Create: {
              id: promptObject.id,
              name: promptObject.name,
              description: promptObject.description,
              promptType: promptObject.type,
              context: promptObject.context,
              promptProject: project.id,
              state: PromptState.ACTIVE,
            },
          })
        );
        if (onAlert) onAlert('Prompt created successfully.', 'success');
      } else {
        await pmpt.Replace(
          new PromptReplaceRequest({
            Replace: {
              promptProject: project.id,
              id: promptObject.id,
              name: promptObject.name,
              description: promptObject.description,
              promptType: promptObject.type,
              context: promptObject.context,
              state: PromptState.ACTIVE,
            },
          })
        );
        if (onAlert) onAlert('Prompt updated successfully.', 'success');
      }
      if (postSave) postSave();
    } catch (error: any) {
      if (onAlert) onAlert(error?.message || 'Failed to save prompt', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveContextUp = (index: number) => {
    setPrompt((draft) => {
      if (index > 0) {
        const temp = draft.context[index];
        draft.context[index] = draft.context[index - 1];
        draft.context[index - 1] = temp;
      }
    });
  };

  const handleMoveContextDown = (index: number) => {
    setPrompt((draft) => {
      if (index < draft.context.length - 1) {
        const temp = draft.context[index];
        draft.context[index] = draft.context[index + 1];
        draft.context[index + 1] = temp;
      }
    });
  };

  return (
    <Box sx={{ margin: 'auto', padding: 2, boxShadow: 3, bgcolor: '#f9f9f9' }}>
      <Typography variant='h5' gutterBottom>
        {prompt ? 'Edit Prompt' : 'Create Prompt'}
      </Typography>
      {!prompt && (
        <TextField
          label='ID'
          value={promptObject.id}
          onChange={(e) => handleFieldChange('id', e.target.value)}
          fullWidth
          variant='outlined'
          sx={{ marginBottom: 2 }}
        />
      )}
      <TextField
        label='Name'
        value={promptObject.name}
        onChange={(e) => handleFieldChange('name', e.target.value)}
        fullWidth
        variant='outlined'
        sx={{ marginBottom: 2 }}
      />
      <TextField
        label='Description'
        value={promptObject.description}
        onChange={(e) => handleFieldChange('description', e.target.value)}
        fullWidth
        variant='outlined'
        sx={{ marginBottom: 2 }}
      />
      {/*<Select*/}
      {/*  label='Prompt Type'*/}
      {/*  value={promptObject.type}*/}
      {/*  onChange={(e) => handleFieldChange('type', e.target.value)}*/}
      {/*  fullWidth*/}
      {/*  variant='outlined'*/}
      {/*  sx={{ marginBottom: 2 }}*/}
      {/*>*/}
      {/*  {Object.entries(PromptType)*/}
      {/*    .filter(([k, v]) => Number.isInteger(v))*/}
      {/*    .map(([k, v]) => (*/}
      {/*      <MenuItem key={k} value={v as PromptType}>*/}
      {/*        {k}*/}
      {/*      </MenuItem>*/}
      {/*    ))}*/}
      {/*</Select>*/}
      <Typography variant='h6' gutterBottom>
        Prompt
      </Typography>
      <List>
        {promptObject.context.map((ctx, index) => (
          <ListItem key={index} sx={{ paddingLeft: 0 }}>
            <TextField
              value={ctx}
              onChange={(e) => handleContextChange(index, e.target.value)}
              fullWidth
              multiline
              variant='outlined'
              sx={{ marginRight: 2 }}
            />
            <IconButton
              onClick={() => handleMoveContextUp(index)}
              disabled={index === 0}
              color='primary'
            >
              <ArrowUpward />
            </IconButton>
            <IconButton
              onClick={() => handleMoveContextDown(index)}
              disabled={index === promptObject.context.length - 1}
              color='primary'
            >
              <ArrowDownward />
            </IconButton>
            <IconButton
              onClick={() => handleRemoveContext(index)}
              color='error'
            >
              <Delete />
            </IconButton>
          </ListItem>
        ))}
        <Button
          variant='outlined'
          color='primary'
          onClick={handleAddContext}
          startIcon={<Add />}
        >
          Add Context
        </Button>
      </List>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
        <Button
          variant='outlined'
          color='secondary'
          onClick={() => {
            if (postSave) postSave();
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
const AddPromptModal: React.FC<{ project: PromptProject }> = ({ project }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  return (
    <>
      <Button variant='contained' color='primary' onClick={handleOpen}>
        Add Prompt
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
        <DialogTitle>
          Add New Prompt
          <IconButton
            aria-label='close'
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PromptEditor
            project={project}
            postSave={() =>
              setTimeout(() => globalThis.location.reload(), 1500)
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
const ExportCSVButton: React.FC<{
  project: PromptProject | null;
  parts: Prompt[];
}> = ({ project, parts }) => {
  const handleExport = async () => {
    if (!project) return;
    try {
      const response = await admin.Export(
        new PromptAdminExportRequest({
          promptProjectId: project.id,
          includeInactivePrompts: false,
        })
      );
      const csvContent = response.promptCsv;
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.id || 'project'}_prompts.csv`;
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed', error);
    }
  };

  return (
    <Button
      variant='outlined'
      color='primary'
      onClick={handleExport}
      sx={{ ml: 2 }}
    >
      Export CSV
    </Button>
  );
};

const ImportCSVModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onImportCsv: (csvText: string) => void;
}> = ({ open, onClose, onImportCsv }) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCsvFile(file);
    if (file) {
      file.text().then((text) => setCsvText(text));
    }
  };

  const handleImport = () => {
    if (csvText) {
      onImportCsv(csvText);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Import Prompts from CSV</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select a CSV file containing prompts to import
        </DialogContentText>
        <input
          type='file'
          accept='.csv'
          onChange={handleFileChange}
          style={{ marginTop: 16 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleImport}
          disabled={!csvText}
          variant='contained'
          color='primary'
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TrashCanButton: React.FC<{
  promptId: string;
  onDeleted?: () => void;
  onAlert?: (message: string, severity: 'success' | 'error') => void;
}> = ({ promptId, onDeleted, onAlert }) => {
  const handleDelete = async () => {
    if (
      globalThis.confirm(
        'Are you sure you want to delete this prompt? This action cannot be undone.'
      )
    ) {
      const response = await pmpt
        .Deactivate(
          new PromptDeactivateRequest({ deactivate: { id: promptId } })
        )
        .catch(() => {
          if (onAlert) onAlert('Failed to delete prompt.', 'error');
        });
      if (onAlert) onAlert('Prompt deleted successfully.', 'success');
      setTimeout(() => globalThis.location.reload(), 1000);
    }
  };
  return (
    <IconButton color='error' onClick={handleDelete} aria-label='delete prompt'>
      <Delete />
    </IconButton>
  );
};
export default PromptProjectForm;
